import * as React from 'react';

import {Session} from 'nutshell-core/session';

import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {Label} from 'shells/form/label';
import {SlackMessagePreview} from 'shells/form/slack/slack-message-preview';
import {SlackMessagePreviewContentLead} from 'shells/form/slack/slack-message-preview-content-lead';
import {FormattedNumberField} from 'shells/formatted-number-field/formatted-number-field';

import type {Comparator} from 'nutshell-graphql-types';
import type {
    ClosedLeadSlackMessage as ClosedLeadSlackMessageFragment,
    SlackChannelBasic as SlackChannelBasicFragment,
    SlackMessageAutomation as SlackMessageAutomationFragment,
} from 'nutshell-graphql-types';

import {GraphQLSelectSlackChannels} from '../../../core/select/graphql-select-slack-channels';
import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import {Checkbox} from '../fields/checkbox';
import {ComparitorSelector} from '../fields/comparitor-selector';

import {SlackMessagePreviewMessage} from './slack-message-preview-message';

import styles from '../sales-process-form.module.css';

export type SlackAutomationInput = {
    leadValueCondition: boolean;
    leadValue: number | null | undefined;
    leadValueComparator: Comparator;
    leadValueCurrencyCode: string;
    channelId: string;
    stagesetId: string;
    outcomeType: string;
};

type Props = {
    stagesetId: string;
    parentId: string;
    slackMessageAutomation:
        | ClosedLeadSlackMessageFragment
        | null
        | undefined
        | SlackMessageAutomationFragment
        | null
        | undefined;
    isFetchingLatestVersion: boolean;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
    onSubmit: (arg1: SlackAutomationInput) => Promise<any>;
    onDelete: (id: string) => Promise<any>;
};

type State = {
    leadValueCondition: boolean;
    leadValue: number | null | undefined;
    leadValueComparator: Comparator;
    leadValueCurrencyCode: string;
    selectedSlackChannelId: string;
};

export class FormSlackComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            leadValueCondition: false,
            leadValue: 0,
            leadValueComparator: 'GREATER',
            leadValueCurrencyCode: Session.getInstanceDefaultCurrencyCode(),
            selectedSlackChannelId: '',
        };
        if (props.slackMessageAutomation) {
            // @ts-expect-error
            this.state.selectedSlackChannelId = props.slackMessageAutomation.slackChannel
                ? props.slackMessageAutomation.slackChannel.externalId
                : '';
            const valueCondition = props.slackMessageAutomation.conditions.find((condition) => {
                return (
                    condition.config.__typename === 'ConditionConfigEntityFilter' &&
                    condition.config.filter.fieldName === 'value' &&
                    condition.config.filter.fieldType === 'CURRENCY'
                );
            });
            if (
                valueCondition &&
                // @ts-expect-error
                valueCondition.__typename === 'ConditionConfigEntityFilter' &&
                // @ts-expect-error
                valueCondition.config.filter.__typename === 'EntityFilterFieldConfigCurrency'
            ) {
                // Assert type-name is correct for flow.
                // @ts-expect-error
                this.state.leadValueCondition = true;
                // @ts-expect-error
                this.state.leadValue = valueCondition.config.filter.currencyValue;
                // @ts-expect-error
                this.state.leadValueComparator = valueCondition.config.filter.currencyComparator;
                // @ts-expect-error
                this.state.leadValueCurrencyCode = valueCondition.config.filter.currencyCode;
            }
        }
    }

    render() {
        return (
            <SalesAutomationSidebarFormWrapper
                id={this.props.slackMessageAutomation ? this.props.slackMessageAutomation.id : null}
                isFetchingLatestVersion={this.props.isFetchingLatestVersion}
                onSaveForm={this.handleSaveForm}
                onDelete={{
                    deleteText: 'Remove slack automation',
                    deleteConfirmationTitle:
                        'Are you sure you want to delete this slack automation?',
                    onDeleteConfirm: this.handleDeleteConfirm,
                }}
            >
                <div className={styles['form-input']}>
                    <Label required={true} htmlFor='slackChannels'>
                        Post Slack notification to…
                    </Label>
                    <GraphQLSelectSlackChannels
                        // @ts-expect-error
                        onClearValue={() => this.setState({selectedSlackChannelId: undefined})}
                        onChange={this.handleSlackChannelChange}
                        selectedId={this.state.selectedSlackChannelId}
                    />
                </div>
                <div className={styles['form-input']}>
                    <Checkbox
                        label='Only send message if lead’s value is…'
                        checked={this.state.leadValueCondition}
                        onChange={this.handleLeadValueConditionChange}
                    />
                    <div className={styles['nested-label']}>
                        <ComparitorSelector
                            comparitorValue={this.state.leadValueComparator}
                            onChange={this.handleValueComparatorChange}
                        />
                        <FormattedNumberField
                            // @ts-expect-error
                            id='LeadValue'
                            currencyCode={this.state.leadValueCurrencyCode}
                            value={this.state.leadValue}
                            onChange={this.handleValueChange}
                        />
                    </div>
                </div>
                <CrossBrowserHiddenFormSubmit inputRef={this.props.formSubmitBtnRef} />
                <SlackMessagePreview
                    previewMessage={<SlackMessagePreviewMessage parentId={this.props.parentId} />}
                    previewContent={
                        <SlackMessagePreviewContentLead formKey={this.props.parentId} />
                    }
                />
            </SalesAutomationSidebarFormWrapper>
        );
    }

    handleLeadValueConditionChange = (leadValueCondition: boolean) => {
        this.setState({leadValueCondition});
    };

    handleSlackChannelChange = (channel: SlackChannelBasicFragment) => {
        this.setState({selectedSlackChannelId: channel.externalId});
    };

    handleValueComparatorChange = (option: {value: Comparator}) => {
        this.setState({
            leadValueComparator: option && option.value,
            leadValueCondition: true,
        });
    };

    handleValueChange = (leadValue?: number | null) => {
        this.setState({
            leadValue: leadValue || 0,
            leadValueCondition: true,
        });
    };

    handleDeleteConfirm = (id: string) => {
        return this.props.onDelete(id);
    };

    handleSaveForm = () => {
        return this.props.onSubmit({
            leadValueCondition: this.state.leadValueCondition,
            leadValue: this.state.leadValue,
            leadValueComparator: this.state.leadValueComparator,
            leadValueCurrencyCode: this.state.leadValueCurrencyCode,
            channelId: this.state.selectedSlackChannelId,
            stagesetId: this.props.stagesetId,
            outcomeType: this.props.parentId,
        });
    };
}
